import { Color3, Quaternion, Space, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Nullable } from '@babylonjs/core/types';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { rotationToVector } from '@view/helpers/math-helper';
import { KbViewer } from '@view/kb-viewer';
import { KbGizmo } from './gizmo';
import { KbPlaneRotationGizmo } from './plane-rotation-gizmo';

const tessellation = 32;

/**
 * Gizmo that enables rotating a mesh along 3 axis
 */
export class KbRotationGizmo extends KbGizmo {
    /**
     * Internal gizmo used for interactions on the x axis
     */
    public xGizmo: KbPlaneRotationGizmo | undefined;
    /**
     * Internal gizmo used for interactions on the y axis
     */
    public yGizmo: KbPlaneRotationGizmo | undefined;
    /**
     * Internal gizmo used for interactions on the z axis
     */
    public zGizmo: KbPlaneRotationGizmo | undefined;

    /** Fires an event when any of it's sub gizmos are dragged */
    public onDragStartObservable = new Observable<void>();
    /** Fires an event when any of it's sub gizmos are released from dragging */
    public onDragObservable = new Observable<{ delta: Quaternion }>();
    public onDragEndObservable = new Observable<{ delta: Quaternion }>();

    public gizmoOffset: KbVector | undefined;
    private _attachedSpaceNode: SpaceNode | undefined;
    private _positionSpace: TransformNode | undefined;

    attachTo(target: SpaceNode | KbVector, positionSpace?: SpaceNode, placementOffset?: KbVector) {
        if (!positionSpace && target instanceof SpaceNode && target._parent) {
            positionSpace = target._parent;
        }
        if (positionSpace) {
            this._positionSpace = this.kbViewer.getRendererNode(positionSpace);
        }

        if (target instanceof SpaceNode) {
            this._attachedSpaceNode = target;
            this._updateGizmoPosition();
        } else {
            this._updateGizmoPosition(target, placementOffset);
        }
    }
    updateGizmoPosition(changeTarget?: KbVector, placementOffset?: KbVector) {
        // Need to wait for the next frame because this can be called before the babylon object is actually updated
        this.kbViewer.onNextFrame(() => {
            this._updateGizmoPosition(changeTarget, placementOffset);
        });
    }

    private _updateGizmoPosition(changeTarget?: KbVector, placementOffset?: KbVector) {
        const positionSpaceTransform = this._positionSpace?.computeWorldMatrix(true);
        if (changeTarget) {
            // If target is a vector, then we want to rotate it as a direction, so set the gizmo position to the origin to calculations are done from there
            // But store the position as the offset because we want the gizmo itself to be visually placed on the position space origin
            this.attachMeshAtNodeSpace(
                this.gizmoLayer.utilityLayerScene,
                placementOffset || KbVector.Zero(),
                positionSpaceTransform
            );

            const corr = rotationToVector(changeTarget.toVec3(), Vector3.Up());
            this.gizmoOffset = KbVector.FromVec3(this.attachedMesh!.position);
            this.attachedMesh!.position = Vector3.Zero();
            this.attachedMesh!.rotationQuaternion?.multiplyInPlace(corr);
            this.attachedMesh!.computeWorldMatrix(true);
        } else if (this._attachedSpaceNode) {
            this.attachMeshAtNodeSpace(
                this.gizmoLayer.utilityLayerScene,
                this._attachedSpaceNode.position.add(this._attachedSpaceNode.pivot),
                positionSpaceTransform
            );

            let currentRotation: Quaternion;
            // Rotation gizmo should be rotated to match the current rotation if the target is a node
            if (this._attachedSpaceNode.useEuler) {
                currentRotation = this._attachedSpaceNode.eulerRotation
                    .toVec3()
                    .scale(Math.PI / 180)
                    .toQuaternion();
            } else {
                currentRotation = rotationToVector(this._attachedSpaceNode.rotationAxis.toVec3());
            }
            this.attachedMesh!.rotationQuaternion = (
                positionSpaceTransform
                    ? Quaternion.FromRotationMatrix(positionSpaceTransform.getRotationMatrix())
                    : Quaternion.Identity()
            ).multiply(currentRotation);
        }
        this.gizmoOffset && this.attachedMesh!.position.addInPlace(this.gizmoOffset.toVec3());
        super.updateGizmoPosition();
    }

    public get attachedMesh() {
        return (this.xGizmo || this.yGizmo || this.zGizmo)!.attachedMesh;
    }
    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        if (this.xGizmo) {
            this.xGizmo.attachedMesh = mesh;
        }
        if (this.yGizmo) {
            this.yGizmo.attachedMesh = mesh;
        }
        if (this.zGizmo) {
            this.zGizmo.attachedMesh = mesh;
        }
        if (mesh) {
            this._rootMesh.setPivotPoint(mesh.getPivotPoint(), Space.WORLD);
        }
    }

    private rotationChange: Quaternion;
    /**
     * Creates a RotationGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param tessellation Amount of tessellation to be used when creating rotation circles
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer,
        axes?: Record<'x' | 'y' | 'z', boolean>,
        axisVisibility?: [boolean, boolean, boolean]
    ) {
        super(gizmoLayer);
        let activeGizmos = [];
        if (axisVisibility) {
            if (axisVisibility[0]) {
                this.xGizmo = new KbPlaneRotationGizmo(
                    new Vector3(1, 0, 0),
                    Color3.Red().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.xGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[0];
                this.xGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[0];
                activeGizmos.push(this.xGizmo);
            }
            if (axisVisibility[1]) {
                this.yGizmo = new KbPlaneRotationGizmo(
                    new Vector3(0, 1, 0),
                    Color3.Green().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.yGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[1];
                this.yGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[1];
                activeGizmos.push(this.yGizmo);
            }
            if (axisVisibility[2]) {
                this.zGizmo = new KbPlaneRotationGizmo(
                    new Vector3(0, 0, 1),
                    Color3.Blue().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.zGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[2];
                this.zGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[2];
                activeGizmos.push(this.zGizmo);
            }
        } else {
            this.updateGizmoRotationToMatchAttachedMesh = true;
            if (!axes || axes.x) {
                this.xGizmo = new KbPlaneRotationGizmo(
                    new Vector3(1, 0, 0),
                    Color3.Red().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.xGizmo.updateGizmoRotationToMatchAttachedMesh = true;
                this.xGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            }
            if (!axes || axes.y) {
                this.yGizmo = new KbPlaneRotationGizmo(
                    new Vector3(0, 1, 0),
                    Color3.Green().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.yGizmo.updateGizmoRotationToMatchAttachedMesh = true;
                this.yGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            }
            if (!axes || axes.z) {
                this.zGizmo = new KbPlaneRotationGizmo(
                    new Vector3(0, 0, 1),
                    Color3.Blue().scale(0.5),
                    gizmoLayer,
                    tessellation
                );
                this.zGizmo.updateGizmoRotationToMatchAttachedMesh = true;
                this.zGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            }
            activeGizmos = [this.xGizmo, this.yGizmo, this.zGizmo];
        }

        this.rotationChange = Quaternion.Identity();

        // Relay drag events
        activeGizmos.forEach(gizmo => {
            if (gizmo) {
                gizmo.dragBehavior.onDragStartObservable.add(() => {
                    this.onDragStartObservable.notifyObservers();
                    this.rotationChange = Quaternion.Identity();
                });
                gizmo.updateObservable.add(event => {
                    this.rotationChange.multiplyInPlace(event.delta);
                    this.onDragObservable.notifyObservers({ delta: event.delta });
                });
                gizmo.dragBehavior.onDragEndObservable.add(() => {
                    this.onDragEndObservable.notifyObservers({ delta: this.rotationChange });
                });
            }
        });

        this.attachedMesh = null;
    }

    /**
     * Drag distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public set snapDistance(value: number) {
        if (this.xGizmo) {
            this.xGizmo.snapDistance = value;
        }
        if (this.yGizmo) {
            this.yGizmo.snapDistance = value;
        }
        if (this.zGizmo) {
            this.zGizmo.snapDistance = value;
        }
    }
    public get snapDistance() {
        return (this.xGizmo || this.yGizmo || this.zGizmo)!.snapDistance;
    }

    /**
     * Ratio for the scale of the gizmo (Default: 1)
     */
    public set scaleRatio(value: number) {
        if (this.xGizmo) {
            this.xGizmo.scaleRatio = value;
        }
        if (this.yGizmo) {
            this.yGizmo.scaleRatio = value;
        }
        if (this.zGizmo) {
            this.zGizmo.scaleRatio = value;
        }
    }
    public get scaleRatio() {
        return (this.xGizmo || this.yGizmo || this.zGizmo)!.scaleRatio;
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        if (this.xGizmo) {
            this.xGizmo.dispose();
        }
        if (this.yGizmo) {
            this.yGizmo.dispose();
        }
        if (this.zGizmo) {
            this.zGizmo.dispose();
        }
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
    }

    /**
     * CustomMeshes are not supported by this gizmo
     * @param mesh The mesh to replace the default mesh of the gizmo
     */
    public setCustomMesh(mesh: Mesh) {
        Logger.Error(
            'Custom meshes are not supported on this gizmo, please set the custom meshes on the gizmos contained within this one (gizmo.xGizmo, gizmo.yGizmo, gizmo.zGizmo)'
        );
    }
}
